// src/admin/AdminSideBar.js
import React, { useContext, useEffect, useState } from "react";
import {
  FiHome,
  FiShoppingBag,
  FiBox,
  FiUsers,
  FiTag,
  FiBarChart2,
  FiBell,
  FiSettings,
  FiLogOut,
  FiX,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { AdminLanguageContext } from "./context/AdminLanguageContext";
import { getUnreadNotificationsCount } from "../services/adminNotifications.api";
import { getPendingOrdersCount } from "../services/adminOrders.api";
import { getAdminMessages } from "../services/adminMessages.api";
import { getActiveCouponsCount } from "../services/adminCoupons.api";
import { getAdminUsersActivity } from "../services/adminUserActivity.api";

// HEROICONS (outline)
import {
  HomeIcon as HomeOutline,
  ShoppingBagIcon as ShoppingBagOutline,
  CubeIcon as BoxOutline,
  UsersIcon as UsersOutline,
  TagIcon as TagOutline,
  ChartBarIcon as ChartOutline,
  BellIcon as BellOutline,
  Cog6ToothIcon as SettingsOutline,
} from "@heroicons/react/24/outline";

// HEROICONS (solid)
import {
  HomeIcon as HomeSolid,
  ShoppingBagIcon as ShoppingBagSolid,
  CubeIcon as BoxSolid,
  UsersIcon as UsersSolid,
  TagIcon as TagSolid,
  ChartBarIcon as ChartSolid,
  BellIcon as BellSolid,
  Cog6ToothIcon as SettingsSolid,
} from "@heroicons/react/24/solid";

import {
  TicketIcon as TicketOutline,
} from "@heroicons/react/24/outline";

import {
  TicketIcon as TicketSolid,
} from "@heroicons/react/24/solid";

function AdminSidebar({ isOpen, setIsOpen }) {
  const navigate = useNavigate();
  const { lang } = useContext(AdminLanguageContext);

  const [unreadCount, setUnreadCount] = useState(null);
  const [pendingOrders, setPendingOrders] = useState(null);
  const [openMessages, setOpenMessages] = useState(null);
  const [activeCoupons, setActiveCoupons] = useState(null);

  const [liveUsersCount, setLiveUsersCount] = useState(0);

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const perms = JSON.parse(localStorage.getItem("staffPermissions") || "{}");
  const isAdmin = adminUser.role === "admin";

  const [openDropdown, setOpenDropdown] = useState(null);

  const toggleDropdown = (key) => {
    setOpenDropdown((prev) => (prev === key ? null : key));
  };

  const TEXT = {
    EN: {
      panel: "Admin Panel",
      createDiscount: "Create Discount",
      dashboard: "Home",
      products: "Products",
      orders: "Orders",
      users: "Users",
      customers: "Customers",
      discount: "Discount",
      analytics: "Analytics",
      notifications: "Notifications",
      inventory: "Inventory Alerts",
      messages: "Messages",
      wholesaleRequests: "Wholesale requests",
      settings: "Settings",
      seo: "SEO Manager",
      activity: "Top Banner",
      banners: "Promo Banners",
      logout: "Logout",
      categories: "Collections",
      customerActivity: "Live view",
      promoCodes: "Promo Codes",
      shipping: "Shipping",
      abandonedCarts: "Abandoned checkouts",
      newIn: "New In",
      bestsellers: "Bestsellers",
      onSale: "On Sale",
      makeSale: "Make a Sale",
      removeSale: "Remove Sale",
      segments: "Segments",
      emailTemplatesManager: "Email Templates",
    },
    AR: {
      panel: "لوحة الإدارة",
      createDiscount: "إنشاء خصم",
      dashboard: "الرئيسية",
      products: "المنتجات",
      orders: "الطلبات",
      users: "المستخدمين",
      discount: "الخصومات",
      customers: "العملاء",
      analytics: "التحليلات",
      notifications: "الإشعارات",
      inventory: "تنبيهات المخزون",
      messages: "الرسائل",
      settings: "الإعدادات",
      seo: "إدارة SEO",
      wholesaleRequests: "طلبات الجملة",
      activity: "الشريط العلوي",
      banners: "العروض الترويجية",
      logout: "تسجيل الخروج",
      categories: "المجموعات",
      customerActivity: "عرض مباشر",
      promoCodes: "أكواد الخصم",
      shipping: "الشحن",
      abandonedCarts: "عمليات شراء متروكة",
      newIn: "الجديد",
      bestsellers: "الأكثر مبيعًا",
      onSale: "التخفيضات",
      makeSale: "إضافة تخفيض",
      removeSale: "إزالة التخفيض",
      segments: "الشرائح",
      emailTemplatesManager: "قوالب محتوى البريد",
    },
  }[lang];

  const canProducts = !!(isAdmin || perms.can_manage_products);
  const canOrders = !!(isAdmin || perms.can_manage_orders);
  const canCustomers = !!(isAdmin || perms.can_manage_customers);
  const canStaff = !!(isAdmin || perms.can_manage_staff);
  const canMessages = !!(isAdmin || perms.can_manage_messages);
  const canMarketing = !!(isAdmin || perms.can_manage_marketing);
  const canAnalytics = !!(isAdmin || perms.can_view_analytics);
  const canSEO = !!(isAdmin || perms.can_manage_seo);
  const canBanners = !!(isAdmin || perms.can_manage_banners);
  const canShipping = !!(isAdmin || perms.can_manage_shipping);

  const hasAnyPermission =
  canProducts ||
  canOrders ||
  canCustomers ||
  canStaff ||
  canMessages ||
  canMarketing ||
  canAnalytics ||
  canSEO ||
  canBanners ||
  canShipping;
  
  const LIVE_MINUTES = 5;

const getLatestActivity = (u) => {
  const candidates = [u.lastAction, u.lastVisit].filter(
    (value) => value && value !== "—" && !isNaN(new Date(value).getTime())
  );

  if (!candidates.length) return "—";

  return candidates.sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  )[0];
};

const isLiveUser = (u) => {
  const last = getLatestActivity(u);
  if (!last || last === "—") return false;

  const lastDate = new Date(last);
  const now = new Date();

  const diffMinutes = (now - lastDate) / 1000 / 60;
  return diffMinutes <= LIVE_MINUTES;
};

const getLiveKey = (u) => {
  if (u.email) return `email:${u.email.toLowerCase()}`;
  if (u.phone) return `phone:${u.phone}`;
  if (u.name && u.name !== "Guest") return `name:${u.name.toLowerCase()}`;
  return `guest:${u.id}`;
};

const getLastActivityValue = (u) => {
  const last = getLatestActivity(u);
  if (!last || last === "—") return 0;
  return new Date(last).getTime();
};

const dedupeLiveUsers = (items) => {
  const map = new Map();

  items.forEach((u) => {
    const key = getLiveKey(u);
    const existing = map.get(key);

    if (!existing) {
      map.set(key, u);
      return;
    }

    const existingLast = getLastActivityValue(existing);
    const currentLast = getLastActivityValue(u);

    if (currentLast > existingLast) {
      map.set(key, u);
    } else if (currentLast === existingLast) {
      if ((u.actions || 0) > (existing.actions || 0)) {
        map.set(key, u);
      }
    }
  });

  return Array.from(map.values());
};

const LivePulse = () => (
  <span className="relative flex h-2.5 w-2.5 shrink-0">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
  </span>
);

  useEffect(() => {
    setUnreadCount(null);
    setOpenMessages(null);
    setPendingOrders(null);
    setActiveCoupons(null);

    if (canMessages) {
      getUnreadNotificationsCount()
        .then((res) => setUnreadCount(res.count || 0))
        .catch(() => {});

      getAdminMessages()
        .then((list) => {
          const count = list.filter(
            (m) => m.status === "new" || m.status === "in_progress"
          ).length;
          setOpenMessages(count);
        })
        .catch(() => {});
    }

    if (canOrders) {
      getPendingOrdersCount()
        .then((res) => setPendingOrders(res.count || 0))
        .catch(() => {});
    }

    if (canMarketing) {
      getActiveCouponsCount()
        .then((res) => setActiveCoupons(res.count || 0))
        .catch(() => {});
    }
  }, [canMessages, canOrders, canMarketing]);

  useEffect(() => {
  if (!canAnalytics) {
    setLiveUsersCount(0);
    return;
  }

  let mounted = true;

  const fetchLiveUsers = () => {
    getAdminUsersActivity()
      .then((rows = []) => {
        if (!mounted) return;

        const liveUsers = dedupeLiveUsers(rows.filter(isLiveUser));
        setLiveUsersCount(liveUsers.length);
      })
      .catch(() => {
        if (mounted) setLiveUsersCount(0);
      });
  };

  fetchLiveUsers();

  const interval = setInterval(fetchLiveUsers, 10000);

  return () => {
    mounted = false;
    clearInterval(interval);
  };
}, [canAnalytics]);

  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";

  const isActive = (paths) =>
  paths.some((p) => {
    // exact match
    if (currentPath === p) return true;

    // allow nested routes ONLY if they truly belong
    if (currentPath.startsWith(p + "/")) return true;

    return false;
  });
  
  const navBtn = (active) =>
`group flex items-center gap-2.5 w-full px-3 py-[6px] text-[13px] rounded-md transition
${lang === "AR" ? "flex-row-reverse justify-end text-right" : ""}
${
  active
  ? "bg-white font-semibold text-[#4A4A4A]"
  : "text-[#4A4A4A] font-semibold"
}
hover:bg-[#F7F7F7]`;

  const iconStyle = (active) =>
  `text-[18px] ${
    active ? "text-[#202223]" : "text-gray-700"
  } group-hover:text-[#202223]`;

  const renderIcon = (OutlineIcon, SolidIcon, active) => {
  const Icon = active ? OutlineIcon : SolidIcon;

  

  return (
    <Icon className="w-[18px] h-[18px] text-[#4A4A4A]" />
  );
};

const renderDiscountIcon = (active) => {
  const src = active
    ? "/icons/discountSelected.png"
    : "/icons/discountNotSelected.png";

  return (
    <img
      src={src}
      alt="discount"
      className="w-[20px] h-[20px] object-contain shrink-0"
    />
  );
};

const renderProductsIcon = (active) => {
  const Icon = active ? TagOutline : TagSolid;

  return (
    <div className="relative w-[18px] h-[18px] flex items-center justify-center">
      <Icon
        className={`
  w-[18px] h-[18px]
  text-[#4A4A4A]
  transform scale-x-[-1]
`}
      />
    </div>
  );
};


  const DropdownHeader = ({ icon, label, id, paths, defaultRoute, rightElement }) => {
  const active =
    defaultRoute &&
    currentPath === defaultRoute;

  const handleClick = () => {
    toggleDropdown(id);

    // ✅ navigate to first item
    if (defaultRoute) {
      navigate(defaultRoute);
    }
  };

  return (
    <button onClick={handleClick} className={`${navBtn(active)} relative`}>
      <span
        className={`flex items-center gap-3 w-full min-w-0 ${
          lang === "AR" ? "flex-row-reverse text-right justify-end" : ""
        }`}
      >
        <span className="flex items-center shrink-0">{icon}</span>
        <span className="truncate">{label}</span>
      </span>

      {rightElement && (
        <span
          className={`
            absolute top-1/2 -translate-y-1/2
            ${lang === "AR" ? "left-3" : "right-3"}
          `}
        >
          {rightElement}
        </span>
      )}
    </button>
  );
};

  const SubItem = ({ label, route, index, activeIndex, rightElement }) => {
    const active = isActive([route]);

    return (
      <div className="relative">
{active && (
 <span
  className={`
    absolute
    ${lang === "AR" ? "right-[-14px]" : "left-[-14px]"}
    top-1/2
    -translate-y-1/2
    flex items-center
  `}
>
  {/* curved corner */}
  <span
    className={`
      block
      w-[10px] h-[10px]
      border-b-2 border-[#C4C4C4]
      ${lang === "AR"
        ? "border-r-2 rounded-br-[6px]"
        : "border-l-2 rounded-bl-[6px]"
      }
    `}
  />

  {/* arrow (Shopify style small sharp) */}
 <span
  className={`
    block mt-2
    -ml-[5px]
    w-[6px] h-[6px]
    border-t-2 border-[#C4C4C4]
    ${lang === "AR"
      ? "border-l-2 -rotate-45 -mr-[5px]"
      : "border-r-2 rotate-45"
    }
  `}
/>
</span>
)}

<button
  dir={lang === "AR" ? "rtl" : "ltr"}
  className={`flex items-center justify-between w-full px-3 py-[6px] text-[13px] rounded-md transition
  ${lang === "AR" ? "text-right" : "text-left"}
  ${active
  ? "bg-white text-[#4A4A4A] font-medium"
  : "text-[#686969]"
}
hover:bg-[#F7F7F7]`}
  onClick={() => navigate(route)}
>
  <span
    className={`truncate flex-1 ${
      lang === "AR" ? "text-right" : "text-left"
    }`}
  >
    {label}
  </span>

  {rightElement && (
    <span className="shrink-0">
      {rightElement}
    </span>
  )}

  {/* ✅ Messages badge */}
  {route === "/admin/messages" && openMessages > 0 && (
    <span
      className="min-w-[20px] h-[20px] px-1 rounded-md bg-[#DDDEDD] text-[#626262] text-[11px] flex items-center justify-center font-semibold"
    >
      {openMessages}
    </span>
  )}
</button>
      </div>
    );
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
      />

     <div
className={`fixed top-[56px] ${lang === "AR" ? "right-0" : "left-0"} h-[calc(100vh-56px)] w-[240px] bg-[#EBEBEB] text-[#4A4A4A]
z-50 flex flex-col ${lang === "AR" ? "border-l border-[#EBEBEB]" : "border-r border-[#EBEBEB]"} ${lang === "AR" ? "rounded-tr-xl" : "rounded-tl-xl"}
transform transition-transform duration-300
${lang === "AR" ? "rtl text-right pr-1" : ""}
${isOpen ? "translate-x-0" : (lang === "AR" ? "translate-x-full" : "-translate-x-full")}
lg:translate-x-0`}
>
        {/* HEADER */}
        <div className="px-3 py-3 border-b border-[#EBEBEB] flex justify-end lg:hidden">
            <button
              className="text-[#4A4A4A]text-xl p-1 rounded hover:bg-[#ededee]"
              onClick={() => setIsOpen(false)}
            >
              <FiX />
            </button>
          </div>

       {/* MENU */}
        <div className="flex-1 overflow-y-auto px-2 py-3 space-y-[2px]">
          {!hasAnyPermission ? (
            <div className="text-[#6d7175] text-sm text-center mt-10">
              {lang === "AR" ? "لا توجد صلاحيات متاحة" : "No permissions assigned"}
            </div>
          ) : (
            <>
              {canAnalytics && (
                <button
                    className={navBtn(isActive(["/admin/dashboard"]))}
                    onClick={() => navigate("/admin/dashboard")}
                  >
                    {renderIcon(
  HomeOutline,
  HomeSolid,
  isActive(["/admin/dashboard"])
)}
                    {TEXT.dashboard}
                  </button>
              )}
{canCustomers && (
  <button
    className={navBtn(isActive(["/admin/wholesale-requests"]))}
    onClick={() => navigate("/admin/wholesale-requests")}
  >
    {renderIcon(
      UsersOutline,
      UsersSolid,
      isActive(["/admin/wholesale-requests"])
    )}
    {TEXT.wholesaleRequests}
  </button>
)}
              {canOrders && (
  <>
    <div className="relative">

      <DropdownHeader
        id="orders"
        icon={renderIcon(
  ShoppingBagOutline,
  ShoppingBagSolid,
  isActive(["/admin/orders"])
)}
        label={TEXT.orders}
        paths={[
          "/admin/orders",
          "/admin/abandoned-carts"
        ]}
        defaultRoute="/admin/orders"
      />

      {pendingOrders > 0 && (
        <span
          className={`
          absolute
          top-2
          ${lang === "AR" ? "left-2" : "right-2"}
          min-w-[20px]
          h-[20px]
          px-1
          rounded-md
          bg-[#DDDEDD]
          text-[#626262]
          text-[11px]
          flex items-center justify-center
          font-semibold
          `}
        >
          {pendingOrders}
        </span>
      )}

    </div>

   {(openDropdown === "orders" || isActive([
  "/admin/orders",
  "/admin/abandoned-carts"
])) && (() => {
  const items = [
  { label: TEXT.abandonedCarts, route: "/admin/abandoned-carts" },
];

  const activeIndex = items.findIndex((item) => isActive([item.route]));

  return (
    <div className={`relative mt-1 space-y-[2px] ${lang === "AR" ? "mr-7" : "ml-7"}`}>
      {activeIndex !== -1 && (
        <span
  className={`
    absolute top-0
    w-[2px]
    bg-[#C4C4C4]
    ${lang === "AR" ? "right-[-14px]" : "left-[-14px]"}
  `}
  style={{
    height: `${(activeIndex * 34) + 17}px`,
    borderBottomLeftRadius: lang === "EN" ? "10px" : "0px",
    borderBottomRightRadius: lang === "AR" ? "10px" : "0px",
  }}
/>
      )}

      {items.map((item, i) => (
        <SubItem
          key={item.route}
          label={item.label}
          route={item.route}
          index={i}
          activeIndex={activeIndex}
        />
      ))}
    </div>
  );
})()}
  </>
)}

{canProducts && (
                <>
                  <DropdownHeader
                    id="products"
                    icon={renderProductsIcon(
  isActive(["/admin/products"])
)}
                    label={TEXT.products}
                    paths={[
  "/admin/products",
  "/admin/categories",
  "/admin/inventory", 
  "/admin/products/new-in",
  "/admin/products/bestsellers",
  "/admin/products/on-sale",
]}
                    defaultRoute="/admin/products" 
                  />

                  {(openDropdown === "products" || isActive([
  "/admin/products",
  "/admin/categories",
  "/admin/inventory",
  "/admin/products/new-in",
  "/admin/products/bestsellers",
  "/admin/products/on-sale"
])) && (() => {
  const items = [
  { label: TEXT.categories, route: "/admin/categories" },
  { label: lang === "AR" ? "المخزون" : "Inventory", route: "/admin/inventory" },
  { label: TEXT.newIn, route: "/admin/products/new-in" },
  { label: TEXT.bestsellers, route: "/admin/products/bestsellers" },
  { label: TEXT.onSale, route: "/admin/products/on-sale" },
];

  const activeIndex = items.findIndex((item) => isActive([item.route]));

  return (
    <div className={`relative mt-1 space-y-[2px] ${lang === "AR" ? "mr-7" : "ml-7"}`}>
      {activeIndex !== -1 && (
        <span
  className={`
    absolute top-0
    w-[2px]
    bg-[#C4C4C4]
    ${lang === "AR" ? "right-[-14px]" : "left-[-14px]"}
  `}
  style={{
    height: `${(activeIndex * 34) + 17}px`,
    borderBottomLeftRadius: lang === "EN" ? "10px" : "0px",
    borderBottomRightRadius: lang === "AR" ? "10px" : "0px",
  }}
/>
      )}

      {items.map((item, i) => (
        <SubItem
          key={item.route}
          label={item.label}
          route={item.route}
          index={i}
          activeIndex={activeIndex}
        />
      ))}
    </div>
  );
})()}
                </>
              )}

{(canStaff || canCustomers) && (
  <>
    <DropdownHeader
  id="users"
  icon={renderIcon(
    UsersOutline,
    UsersSolid,
    isActive([
  "/admin/segments",
  "/admin/users",
  "/admin/customers",
  "/admin/users-activity"
])
  )}
  label={TEXT.customers}
  defaultRoute="/admin/customers"
/>

   {(openDropdown === "users" || isActive([
  "/admin/segments",
  "/admin/customers",
])) && (() => {
 const items = [
  { label: TEXT.segments, route: "/admin/segments" },
];

  const activeIndex = items.findIndex((item) => isActive([item.route]));

  return (
    <div className={`relative mt-1 space-y-[2px] ${lang === "AR" ? "mr-7" : "ml-7"}`}>
      {activeIndex !== -1 && (
        <span
  className={`
    absolute top-0
    w-[2px]
    bg-[#C4C4C4]
    ${lang === "AR" ? "right-[-14px]" : "left-[-14px]"}
  `}
  style={{
    height: `${(activeIndex * 34) + 17}px`,
    borderBottomLeftRadius: lang === "EN" ? "10px" : "0px",
    borderBottomRightRadius: lang === "AR" ? "10px" : "0px",
  }}
/>
      )}

      {items.map((item, i) => (
        <SubItem
          key={item.route}
          label={item.label}
          route={item.route}
          index={i}
          activeIndex={activeIndex}
        />
      ))}
    </div>
  );
})()}
  </>
)}


{canMarketing && (
  <>
    <DropdownHeader
      id="discount"
      icon={renderDiscountIcon(
  isActive(["/admin/discounts"])
)}
      label={TEXT.discount}
      paths={[
  "/admin/discounts",
  "/admin/sale-banner"
]}
      defaultRoute="/admin/discounts"
    />

    {(openDropdown === "discount" || isActive([
  "/admin/discounts",
  "/admin/sale-banner"
])) && (() => {
  const items = [
  { label: TEXT.banners, route: "/admin/sale-banner" },
];

  const activeIndex = items.findIndex((item) => isActive([item.route]));

  return (
    <div className={`relative mt-1 space-y-[2px] ${lang === "AR" ? "mr-7" : "ml-7"}`}>
      {activeIndex !== -1 && (
       <span
  className={`
    absolute top-0
    w-[2px]
    bg-[#C4C4C4]
    ${lang === "AR" ? "right-[-14px]" : "left-[-14px]"}
  `}
  style={{
    height: `${(activeIndex * 34) + 17}px`,
    borderBottomLeftRadius: lang === "EN" ? "10px" : "0px",
    borderBottomRightRadius: lang === "AR" ? "10px" : "0px",
  }}
/>
      )}

      {items.map((item, i) => (
        <SubItem
          key={item.route}
          label={item.label}
          route={item.route}
          index={i}
          activeIndex={activeIndex}
        />
      ))}
    </div>
  );
})()}
  </>
)}
              {canAnalytics && (() => {
  const analyticsPaths = [
    "/admin/analytics",
    "/admin/reports",
    "/admin/analytics/products",
    "/admin/users-activity",
  ];

  const analyticsDropdownOpen =
    openDropdown === "analytics" || isActive(analyticsPaths);

  const hasLiveUsers = liveUsersCount > 0;

  return (
    <>
      <DropdownHeader
        id="analytics"
        icon={renderIcon(
          ChartOutline,
          ChartSolid,
          isActive(["/admin/analytics"])
        )}
        label={TEXT.analytics}
        paths={analyticsPaths}
        defaultRoute="/admin/analytics"
        rightElement={
          hasLiveUsers && !analyticsDropdownOpen ? <LivePulse /> : null
        }
      />

      {analyticsDropdownOpen && (() => {
        const items = [
          {
            label: lang === "EN" ? "Reports" : "التقارير",
            route: "/admin/reports",
          },
          {
            label: lang === "EN" ? "Product Analytics" : "تحليلات المنتجات",
            route: "/admin/analytics/products",
          },
          {
            label: TEXT.customerActivity,
            route: "/admin/users-activity",
            showLivePulse: hasLiveUsers,
          },
        ];

        const activeIndex = items.findIndex((item) => isActive([item.route]));

        return (
          <div className={`relative mt-1 space-y-[2px] ${lang === "AR" ? "mr-7" : "ml-7"}`}>
            {activeIndex !== -1 && (
              <span
                className={`
                  absolute top-0
                  w-[2px]
                  bg-[#C4C4C4]
                  ${lang === "AR" ? "right-[-14px]" : "left-[-14px]"}
                `}
                style={{
                  height: `${(activeIndex * 34) + 17}px`,
                  borderBottomLeftRadius: lang === "EN" ? "10px" : "0px",
                  borderBottomRightRadius: lang === "AR" ? "10px" : "0px",
                }}
              />
            )}

            {items.map((item, i) => (
              <SubItem
                key={item.route}
                label={item.label}
                route={item.route}
                index={i}
                activeIndex={activeIndex}
                rightElement={item.showLivePulse ? <LivePulse /> : null}
              />
            ))}
          </div>
        );
      })()}
    </>
  );
})()}

              {canMessages && (
  <>
    <div className="relative">
      <DropdownHeader
        id="alerts"
        icon={renderIcon(
          BellOutline,
          BellSolid,
          isActive(["/admin/notifications"])
        )}
        label={TEXT.notifications}
        paths={[
          "/admin/notifications",
          "/admin/messages",
        ]}
        defaultRoute="/admin/notifications"
      />

      {unreadCount > 0 && (
        <span
          className={`
            absolute
            top-2
            ${lang === "AR" ? "left-3" : "right-3"}
            min-w-[20px]
            h-[20px]
            px-1
            rounded-md
            bg-[#DDDEDD]
            text-[#626262]
            text-[11px]
            flex items-center justify-center
            font-semibold
          `}
        >
          {unreadCount}
        </span>
      )}
    </div>

                  {(openDropdown === "alerts" || isActive([
  "/admin/notifications",
  "/admin/messages"
])) && (() => {
  const items = [
  { label: TEXT.messages, route: "/admin/messages" },
];

  const activeIndex = items.findIndex((item) => isActive([item.route]));

  return (
    <div className={`relative mt-1 space-y-[2px] ${lang === "AR" ? "mr-7" : "ml-7"}`}>
      {activeIndex !== -1 && (
       <span
  className={`
    absolute top-0
    w-[2px]
    bg-[#C4C4C4]
    ${lang === "AR" ? "right-[-14px]" : "left-[-14px]"}
  `}
  style={{
    height: `${(activeIndex * 34) + 17}px`,
    borderBottomLeftRadius: lang === "EN" ? "10px" : "0px",
    borderBottomRightRadius: lang === "AR" ? "10px" : "0px",
  }}
/>
      )}

      {items.map((item, i) => (
        <SubItem
          key={item.route}
          label={item.label}
          route={item.route}
          index={i}
          activeIndex={activeIndex}
        />
      ))}
    </div>
  );
})()}
                </>
              )}

              {(canMarketing || canSEO || canBanners) && (
  <>
    <DropdownHeader
      id="settings"
      icon={renderIcon(
        SettingsOutline,
        SettingsSolid,
        isActive(["/admin/settings"])
      )}
      label={TEXT.settings}
      paths={[
        "/admin/settings",
        "/admin/top-banner",
        "/admin/email-template-manager",
        "/admin/seo",
        "/admin/users",
      ]}
      defaultRoute="/admin/settings"
    />

    {(openDropdown === "settings" || isActive([
      "/admin/settings",
      "/admin/top-banner",
      "/admin/email-template-manager",
      "/admin/seo",
      "/admin/users",
    ])) && (() => {
      const items = [
  { label: TEXT.activity, route: "/admin/top-banner" },
  { label: TEXT.seo, route: "/admin/seo" },
  ...(canStaff ? [{ label: TEXT.users, route: "/admin/users" }] : []),
];

  const activeIndex = items.findIndex((item) => isActive([item.route]));

  return (
    <div className={`relative mt-1 space-y-[2px] ${lang === "AR" ? "mr-7" : "ml-7"}`}>
      {activeIndex !== -1 && (
       <span
  className={`
    absolute top-0
    w-[2px]
    bg-[#C4C4C4]
    ${lang === "AR" ? "right-[-14px]" : "left-[-14px]"}
  `}
  style={{
    height: `${(activeIndex * 34) + 17}px`,
    borderBottomLeftRadius: lang === "EN" ? "10px" : "0px",
    borderBottomRightRadius: lang === "AR" ? "10px" : "0px",
  }}
/>
      )}

      {items.map((item, i) => (
        <SubItem
          key={item.route}
          label={item.label}
          route={item.route}
          index={i}
          activeIndex={activeIndex}
        />
      ))}
    </div>
  );
})()}
                </>
              )}
            </>
          )}
        </div>

          {/* LOGOUT */}
        <div className="px-2 py-3 border-t border-[#EBEBEB]">
          <button
            className="flex items-center gap-3 w-full px-3 py-[6px] text-[14px] rounded-md hover:bg-[#ededee] text-[#4A4A4A]"
            onClick={() => {
              localStorage.removeItem("adminToken");
              localStorage.removeItem("adminUser");
              localStorage.removeItem("staffPermissions");
              navigate("/admin/login");
            }}
          >
            <FiLogOut className="text-[#4A4A4A]" /> {TEXT.logout}
          </button>
        </div>
      </div>
    </>
  );
}

export default AdminSidebar;
